import { inject } from '@angular/core';
import { AuthGuardConfig, NdwAuthConfig, OnUnauthorized } from '@ndwnu/auth';
import { environment } from '@env/environment';
import { Router } from '@angular/router';
import { overviewPath } from '@daisi/app.paths';
import { AuthService } from '@services/auth/auth.service';

const FORBIDDEN_KEY = 'forbidden_logout';

const hasForbiddenCookie = (): boolean => document.cookie.includes(FORBIDDEN_KEY);
const setForbiddenCookie = (): void => {
  document.cookie = `${FORBIDDEN_KEY}=true;max-age=30;path=/`;
};
const clearForbiddenCookie = (): void => {
  document.cookie = `${FORBIDDEN_KEY}=;max-age=0;path=/`;
};

const onUnauthorized = (auth: OnUnauthorized) => {
  const authService = inject(AuthService);
  if (auth.type === 'forbidden') {
    if (hasForbiddenCookie()) {
      // Second forbidden response within the cookie window: log out silently instead of warning
      // again, and stop here so the loop breaker can arm again on the next attempt.
      clearForbiddenCookie();
      authService.logout();
      return;
    }
    setForbiddenCookie();
    window.alert('Je hebt geen toegang tot deze pagina.');
    authService.logout();
  } else {
    window.alert('Je bent niet ingelogd. Log in om verder te gaan.');
    authService.login();
  }
};

const onRoleMissing = (auth: OnUnauthorized) => {
  window.alert(
    'Je hebt niet de benodigde rechten om deze pagina te bekijken. Neem contact op met de servicedesk als dat wel zo zou moeten zijn.',
  );
  inject(Router).navigate([overviewPath]);
};

export const authConfig: NdwAuthConfig = {
  authConfig: {
    issuer: environment.auth?.issuer,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    clientId: environment.auth?.clientId,
    responseType: 'code',
    scope: 'openid profile email roles',
    showDebugInformation: environment.environment === 'local',
  },
  moduleConfig: {
    resourceServer: {
      allowedUrls: ['/api', '/data', '/data-realtime'],
      sendAccessToken: true,
    },
  },
  onUnauthorized: onUnauthorized,
};

export const defaultAuthGuardConfig: AuthGuardConfig = {
  forceLogin: true,
  roles: ['user'],
  checkRoles: 'any',
  onUnauthorized: onRoleMissing,
};

export const incidentsAuthGuardConfig: AuthGuardConfig = {
  ...defaultAuthGuardConfig,
  roles: ['incidents'],
  onUnauthorized: onUnauthorized,
};

export const trafficAuthGuardConfig: AuthGuardConfig = {
  ...defaultAuthGuardConfig,
  roles: ['traffic'],
};

export const roadworksAuthGuardConfig: AuthGuardConfig = {
  ...defaultAuthGuardConfig,
  roles: ['roadworks'],
};

export const userAdminAuthGuardConfig: AuthGuardConfig = {
  ...defaultAuthGuardConfig,
  roles: ['user_admin'],
};

export const adminAuthGuardConfig: AuthGuardConfig = {
  ...defaultAuthGuardConfig,
  roles: ['admin'],
};

export const dashboardAuthGuardConfig: AuthGuardConfig = {
  ...defaultAuthGuardConfig,
  roles: ['dashboard'],
};
